import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  AlertCircle, ArrowRight, ChevronDown, ChevronLeft, ChevronRight, Mail, MapPin, Phone, Play, Quote, X, Info, Newspaper,
} from 'lucide-react';
import { resolveImageUrl } from '../../lib/resolveImageUrl';
import { cachedGet } from '../../lib/apiCache';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { formatDeDate } from '../../utils/statusLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Block shape: { id, type, data: {...} } — gleiche Form für `site_elements`
// (Seiten-Builder) und `pages.<page>.blocks` (Galerie, ältere Blöcke).

/* ───────────────────────── Helfer ───────────────────────── */

/** Nur Links mit bekannten Schemata durchlassen (Schutz vor javascript:-Links). */
export const safeHref = (href) => {
  const v = (href || '').trim();
  if (!v) return '';
  if (v.startsWith('//')) return '';
  if (v.startsWith('/') || v.startsWith('#')) return v;
  if (/^(https?:|mailto:|tel:)/i.test(v)) return v;
  return '';
};

const isInternal = (href) => href.startsWith('/') && !href.startsWith('//');

/** Link, der intern per Router und extern per <a> läuft. */
export const SmartLink = ({ href, newTab, className, style, children, ...rest }) => {
  const safe = safeHref(href);
  if (!safe) return <span className={className} style={style} {...rest}>{children}</span>;
  if (isInternal(safe) && !newTab) {
    return <Link to={safe} className={className} style={style} {...rest}>{children}</Link>;
  }
  const external = /^https?:/i.test(safe) || newTab;
  return (
    <a
      href={safe}
      className={className}
      style={style}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...rest}
    >
      {children}
    </a>
  );
};

/** Bild mit mobiler/desktop-Variante (Mediathek-URLs liefern `?w=mobile|desktop`). */
export const imgProps = (url) => {
  const src = resolveImageUrl(url);
  if (typeof url === 'string' && url.includes('/api/blog/image/') && url.includes('w=desktop')) {
    return {
      src,
      srcSet: `${resolveImageUrl(url.replace('w=desktop', 'w=mobile'))} 768w, ${src} 1600w`,
      sizes: '(max-width: 768px) 100vw, 1200px',
      loading: 'lazy',
      decoding: 'async',
    };
  }
  return { src, loading: 'lazy', decoding: 'async' };
};

const SPACE = { none: 'mb-0', sm: 'mb-4', md: 'mb-8', lg: 'mb-14' };
const WIDTH = { narrow: 'max-w-3xl mx-auto', normal: '', full: '' };
const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
const JUSTIFY = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };
const RATIO = { '16:9': 'aspect-video', '4:3': 'aspect-[4/3]', '1:1': 'aspect-square', '3:2': 'aspect-[3/2]' };
const COLS = { 1: 'grid-cols-1', 2: 'grid-cols-1 sm:grid-cols-2', 3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', 4: 'grid-cols-2 lg:grid-cols-4' };

const Frame = ({ data, defaultSpace = 'md', defaultWidth = 'normal', children }) => (
  <section
    id={data.anchor || undefined}
    className={`${SPACE[data.space || defaultSpace] ?? SPACE.md} ${WIDTH[data.width || defaultWidth] || ''}`}
  >
    {children}
  </section>
);

const SectionTitle = ({ data, align = 'center' }) => {
  if (!data.title && !data.subtitle) return null;
  return (
    <div className={`mb-6 ${ALIGN[data.align || align]}`}>
      {data.title && <h2 className="text-2xl sm:text-3xl font-bold text-white">{data.title}</h2>}
      {data.subtitle && <p className="text-[#a1a1aa] mt-2 max-w-2xl mx-auto">{data.subtitle}</p>}
    </div>
  );
};

const BTN_BASE = 'inline-flex items-center justify-center gap-2 px-6 h-11 rounded-lg font-medium text-sm transition-colors';
const BTN_STYLES = {
  primary: 'bg-[#0094ff] hover:bg-[#33a9ff] text-white',
  outline: 'border border-[#0094ff]/60 text-[#0094ff] hover:bg-[#0094ff]/10',
  light: 'bg-white text-[#161313] hover:bg-white/90',
  ghost: 'text-white underline underline-offset-4 hover:text-[#33a9ff]',
};

export const BlockButton = ({ label, link, style = 'primary', newTab, arrow = true }) => {
  if (!label) return null;
  return (
    <SmartLink href={link} newTab={newTab} className={`${BTN_BASE} ${BTN_STYLES[style] || BTN_STYLES.primary}`}>
      {label}{arrow && safeHref(link) && style !== 'ghost' && <ArrowRight className="w-4 h-4" />}
    </SmartLink>
  );
};

/* ───────────────────────── Blöcke ───────────────────────── */

const HeadingBlock = ({ data }) => {
  const Tag = ['h1', 'h2', 'h3'].includes(data.level) ? data.level : 'h1';
  const size = { h1: 'text-3xl sm:text-4xl', h2: 'text-2xl sm:text-3xl', h3: 'text-xl sm:text-2xl' }[Tag];
  return (
    <Frame data={data} defaultSpace="lg">
      <div className={ALIGN[data.align || 'center']}>
        {data.eyebrow && (
          <span className="text-[#0094ff] text-sm font-medium tracking-widest uppercase mb-3 block">{data.eyebrow}</span>
        )}
        {data.title && <Tag className={`${size} font-bold text-white mb-3`}>{data.title}</Tag>}
        {data.subtitle && (
          <p className={`text-[#a1a1aa] max-w-xl ${data.align === 'left' ? '' : data.align === 'right' ? 'ml-auto' : 'mx-auto'}`}>{data.subtitle}</p>
        )}
      </div>
    </Frame>
  );
};

const TextBlock = ({ data }) => (
  <Frame data={data} defaultWidth="narrow">
    <div
      className={`article-content text-[#a1a1aa] ${ALIGN[data.align || 'left']}`}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.html || '') }}
    />
  </Frame>
);

const IMAGE_SIZE = { small: 'max-w-xs', medium: 'max-w-xl', large: 'max-w-3xl', full: 'max-w-full' };

const ImageBlock = ({ data }) => {
  if (!data.url) return null;
  const ratio = RATIO[data.ratio];
  const img = (
    <img
      {...imgProps(data.url)}
      alt={data.alt || ''}
      className={`${data.rounded === false ? '' : 'rounded-lg'} w-full ${ratio ? `${ratio} object-cover` : 'h-auto'}`}
    />
  );
  return (
    <Frame data={data}>
      <figure className={`flex flex-col ${data.align === 'left' ? 'items-start' : data.align === 'right' ? 'items-end' : 'items-center'}`}>
        <div className={`w-full ${IMAGE_SIZE[data.size] || IMAGE_SIZE.full}`}>
          {data.link ? <SmartLink href={data.link} newTab={data.new_tab} className="block hover:opacity-90 transition-opacity">{img}</SmartLink> : img}
        </div>
        {data.caption && <figcaption className="text-[#a1a1aa] text-sm mt-2">{data.caption}</figcaption>}
      </figure>
    </Frame>
  );
};

const GalleryBlock = ({ data }) => {
  const images = (data.images || []).filter((i) => i && i.url);
  const [open, setOpen] = useState(null);
  const lightbox = data.lightbox !== false;
  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(null);
      if (e.key === 'ArrowRight') setOpen((i) => (i + 1) % images.length);
      if (e.key === 'ArrowLeft') setOpen((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, images.length]);
  if (images.length === 0) return null;
  const ratio = RATIO[data.ratio || '4:3'] || RATIO['4:3'];
  return (
    <Frame data={data}>
      <SectionTitle data={data} />
      <div className={`grid gap-3 ${COLS[data.columns || 3] || COLS[3]}`}>
        {images.map((img, i) => {
          const tile = (
            <img {...imgProps(img.url)} alt={img.alt || ''} className={`w-full ${ratio} object-cover rounded-lg`} />
          );
          return (
            <figure key={`${img.url}-${i}`} className="group">
              {img.link ? (
                <SmartLink href={img.link} className="block overflow-hidden rounded-lg hover:opacity-90 transition-opacity">{tile}</SmartLink>
              ) : lightbox ? (
                <button type="button" onClick={() => setOpen(i)} className="block w-full overflow-hidden rounded-lg cursor-zoom-in" aria-label={img.alt || `Bild ${i + 1} vergrößern`}>{tile}</button>
              ) : tile}
              {img.caption && <figcaption className="text-[#a1a1aa] text-xs mt-1.5">{img.caption}</figcaption>}
            </figure>
          );
        })}
      </div>
      {open !== null && images[open] && (
        <div className="fixed inset-0 z-overlay-nav bg-black/90 flex items-center justify-center p-4" onClick={() => setOpen(null)} role="dialog" aria-modal="true">
          <button type="button" className="absolute top-4 right-4 text-white p-2" onClick={() => setOpen(null)} aria-label="Schließen"><X className="w-7 h-7" /></button>
          <img src={resolveImageUrl(images[open].url)} alt={images[open].alt || ''} className="max-w-full max-h-[88vh] object-contain rounded" onClick={(e) => e.stopPropagation()} />
          {images[open].caption && <p className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm px-4">{images[open].caption}</p>}
        </div>
      )}
    </Frame>
  );
};

const ImageTextBlock = ({ data }) => {
  const right = data.side === 'right';
  return (
    <Frame data={data} defaultSpace="lg">
      <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        {data.url && (
          <div className={right ? 'md:order-2' : ''}>
            <img {...imgProps(data.url)} alt={data.alt || data.title || ''} className="w-full h-auto rounded-xl" />
          </div>
        )}
        <div className={data.url ? '' : 'md:col-span-2'}>
          {data.eyebrow && <span className="text-[#0094ff] text-sm font-medium tracking-widest uppercase mb-2 block">{data.eyebrow}</span>}
          {data.title && <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{data.title}</h2>}
          {data.html && (
            <div className="article-content text-[#a1a1aa]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.html) }} />
          )}
          {data.button_label && (
            <div className="mt-6"><BlockButton label={data.button_label} link={data.button_link} /></div>
          )}
        </div>
      </div>
    </Frame>
  );
};

const ButtonBlock = ({ data }) => {
  // Neu: `buttons: [...]`; älter: einzelnes label/link.
  const buttons = Array.isArray(data.buttons)
    ? data.buttons
    : (data.label ? [{ label: data.label, link: data.link, style: 'primary' }] : []);
  const visible = buttons.filter((b) => b && b.label);
  if (visible.length === 0) return null;
  return (
    <Frame data={data}>
      <div className={`flex flex-wrap gap-3 ${JUSTIFY[data.align || 'center']}`}>
        {visible.map((b, i) => <BlockButton key={i} label={b.label} link={b.link} style={b.style} newTab={b.new_tab} />)}
      </div>
    </Frame>
  );
};

const BannerBlock = ({ data }) => {
  if (!data.text) return null;
  const Icon = data.icon === false ? null : AlertCircle;
  return (
    <Frame data={data}>
      <div className="rounded-lg p-4 text-center" style={{ background: data.bg_color || '#0094ff', color: data.text_color || '#fff' }}>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {Icon && <Icon className="w-5 h-5 shrink-0" />}
          <p className="font-medium text-sm">{data.text}</p>
          {data.link && (
            <SmartLink href={data.link} newTab={data.new_tab} className="underline text-sm font-semibold ml-2">{data.link_label || 'Details'} &rarr;</SmartLink>
          )}
        </div>
      </div>
    </Frame>
  );
};

const HERO_HEIGHT = { sm: 'min-h-[240px]', md: 'min-h-[340px]', lg: 'min-h-[480px]' };

const HeroBlock = ({ data }) => {
  const overlay = Math.max(0, Math.min(90, Number(data.overlay ?? 55))) / 100;
  return (
    <Frame data={data} defaultSpace="lg">
      <div className={`relative overflow-hidden rounded-2xl flex items-center ${HERO_HEIGHT[data.height || 'md']} bg-[#231f1f]`}>
        {data.url && <img {...imgProps(data.url)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${overlay})` }} />
        <div className={`relative w-full px-6 sm:px-12 py-12 ${ALIGN[data.align || 'center']}`}>
          {data.eyebrow && <span className="text-[#33a9ff] text-sm font-medium tracking-widest uppercase mb-3 block">{data.eyebrow}</span>}
          {data.title && <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">{data.title}</h2>}
          {data.subtitle && <p className={`text-white/80 text-lg max-w-2xl mb-6 ${data.align === 'left' ? '' : data.align === 'right' ? 'ml-auto' : 'mx-auto'}`}>{data.subtitle}</p>}
          <div className={`flex flex-wrap gap-3 ${JUSTIFY[data.align || 'center']}`}>
            <BlockButton label={data.button_label} link={data.button_link} />
            <BlockButton label={data.button2_label} link={data.button2_link} style="light" />
          </div>
        </div>
      </div>
    </Frame>
  );
};

const NewsBlock = ({ data }) => {
  const items = (data.items || []).filter((n) => n && (n.title || n.text));
  if (items.length === 0) return null;
  const grid = data.layout === 'grid';
  return (
    <Frame data={data}>
      <SectionTitle data={data} align="left" />
      <div className={grid ? `grid gap-5 ${COLS[3]}` : 'space-y-4'}>
        {items.map((n, i) => (
          <article key={i} className={`bg-[#231f1f] border border-[#605e5e]/10 rounded-xl overflow-hidden ${!grid && n.url ? 'sm:flex' : ''}`}>
            {n.url && (
              <img {...imgProps(n.url)} alt={n.title || ''} className={grid ? 'w-full aspect-video object-cover' : 'w-full sm:w-56 sm:shrink-0 aspect-video sm:aspect-auto object-cover'} />
            )}
            <div className="p-5">
              <div className="flex items-center gap-2 text-xs text-[#0094ff] mb-1.5">
                <Newspaper className="w-3.5 h-3.5" />
                {n.date && <time>{formatDeDate(n.date)}</time>}
              </div>
              {n.title && <h3 className="text-white font-semibold text-lg mb-1.5">{n.title}</h3>}
              {n.text && <p className="text-[#a1a1aa] text-sm leading-relaxed whitespace-pre-line">{n.text}</p>}
              {n.link && (
                <SmartLink href={n.link} className="inline-flex items-center gap-1 mt-3 text-[#0094ff] text-sm font-medium hover:underline">
                  {n.link_label || 'Mehr erfahren'} <ArrowRight className="w-3.5 h-3.5" />
                </SmartLink>
              )}
            </div>
          </article>
        ))}
      </div>
    </Frame>
  );
};

/** Aktionen & Neuigkeiten: Karten mit Kennzeichen, optional zeitlich begrenzt, als Wischleiste oder Raster. */
const PromosBlock = ({ data }) => {
  const trackRef = useRef(null);
  const today = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD in lokaler Zeit
  const items = (data.items || []).filter((p) => (
    p && (p.title || p.text)
    && (!p.from || p.from <= today)
    && (!p.until || p.until >= today)
  ));
  if (items.length === 0) return null;
  const grid = data.layout === 'grid';
  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * Math.min(360, el.clientWidth * 0.9), behavior: 'smooth' });
  };
  const card = (p, i) => {
    const body = (
      <>
        {p.url && (
          <div className="relative">
            <img {...imgProps(p.url)} alt={p.alt || p.title || ''} className="w-full aspect-[16/10] object-cover" />
            {p.badge && (
              <span className="absolute top-3 left-3 rounded-full bg-[#0094ff] px-3 py-1 text-xs font-semibold text-white">{p.badge}</span>
            )}
          </div>
        )}
        <div className="p-5 flex flex-col flex-1">
          {!p.url && p.badge && (
            <span className="self-start rounded-full bg-[#0094ff]/15 border border-[#0094ff]/30 px-3 py-1 text-xs font-semibold text-[#33a9ff] mb-3">{p.badge}</span>
          )}
          {p.title && <h3 className="text-white font-semibold text-lg leading-snug mb-1.5">{p.title}</h3>}
          {p.text && <p className="text-[#a1a1aa] text-sm leading-relaxed whitespace-pre-line">{p.text}</p>}
          {p.link && (
            <span className="inline-flex items-center gap-1 mt-auto pt-4 text-[#0094ff] text-sm font-medium">
              {p.link_label || 'Mehr erfahren'} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </>
    );
    const cls = `flex flex-col bg-[#231f1f] border border-[#605e5e]/10 rounded-xl overflow-hidden ${grid ? '' : 'snap-start shrink-0 w-[82%] sm:w-[340px]'}`;
    return p.link
      ? <SmartLink key={i} href={p.link} className={`${cls} hover:border-[#0094ff]/40 transition-colors`}>{body}</SmartLink>
      : <div key={i} className={cls}>{body}</div>;
  };
  return (
    <Frame data={data} defaultSpace="lg">
      <div className="flex items-end justify-between gap-4">
        <div className="flex-1"><SectionTitle data={data} align="left" /></div>
        {!grid && items.length > 1 && (
          <div className="hidden sm:flex gap-2 mb-6">
            <button type="button" onClick={() => scrollBy(-1)} aria-label="Zurück" className="w-9 h-9 rounded-full border border-[#605e5e]/40 text-white hover:border-[#0094ff] flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" onClick={() => scrollBy(1)} aria-label="Weiter" className="w-9 h-9 rounded-full border border-[#605e5e]/40 text-white hover:border-[#0094ff] flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      {grid ? (
        <div className={`grid gap-5 ${COLS[data.columns || 3] || COLS[3]}`}>{items.map(card)}</div>
      ) : (
        <div ref={trackRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-1 px-1" data-testid="promos-track">{items.map(card)}</div>
      )}
    </Frame>
  );
};

const CardsBlock = ({ data }) => {
  const cards = (data.cards || []).filter((c) => c && (c.title || c.text || c.url));
  if (cards.length === 0) return null;
  return (
    <Frame data={data} defaultSpace="lg">
      <SectionTitle data={data} />
      <div className={`grid gap-5 ${COLS[data.columns || 3] || COLS[3]}`}>
        {cards.map((c, i) => {
          const body = (
            <>
              {c.url && <img {...imgProps(c.url)} alt={c.alt || c.title || ''} className="w-full aspect-video object-cover" />}
              <div className="p-5">
                {c.title && <h3 className="text-white font-semibold text-lg mb-1.5">{c.title}</h3>}
                {c.text && <p className="text-[#a1a1aa] text-sm leading-relaxed whitespace-pre-line">{c.text}</p>}
                {c.link && (
                  <span className="inline-flex items-center gap-1 mt-3 text-[#0094ff] text-sm font-medium">
                    {c.link_label || 'Mehr erfahren'} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
            </>
          );
          const cls = 'block bg-[#231f1f] border border-[#605e5e]/10 rounded-xl overflow-hidden';
          return c.link
            ? <SmartLink key={i} href={c.link} className={`${cls} hover:border-[#0094ff]/40 transition-colors`}>{body}</SmartLink>
            : <div key={i} className={cls}>{body}</div>;
        })}
      </div>
    </Frame>
  );
};

const QuoteBlock = ({ data }) => {
  if (!data.text) return null;
  return (
    <Frame data={data} defaultWidth="narrow">
      <figure className="bg-[#231f1f] border border-[#605e5e]/10 rounded-xl p-6 sm:p-8 text-center">
        <Quote className="w-8 h-8 text-[#0094ff] mx-auto mb-3" />
        <blockquote className="text-white text-lg sm:text-xl leading-relaxed whitespace-pre-line">{data.text}</blockquote>
        {(data.author || data.role || data.url) && (
          <figcaption className="mt-4 flex items-center justify-center gap-3">
            {data.url && <img {...imgProps(data.url)} alt="" className="w-10 h-10 rounded-full object-cover" />}
            <span className="text-sm text-[#a1a1aa]">
              {data.author && <strong className="text-white font-semibold">{data.author}</strong>}
              {data.role && <span>{data.author ? ' · ' : ''}{data.role}</span>}
            </span>
          </figcaption>
        )}
      </figure>
    </Frame>
  );
};

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#231f1f] border border-[#605e5e]/10 rounded-lg">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 text-white font-medium">
        <span>{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#0094ff] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 article-content text-[#a1a1aa] text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a || '') }} />
      )}
    </div>
  );
};

const FaqBlock = ({ data }) => {
  const items = (data.items || []).filter((i) => i && i.q);
  if (items.length === 0) return null;
  return (
    <Frame data={data} defaultWidth="narrow">
      <SectionTitle data={data} />
      <div className="space-y-3">{items.map((it, i) => <FaqItem key={i} q={it.q} a={it.a} />)}</div>
    </Frame>
  );
};

/** YouTube/Vimeo -> Embed-URL, Dateien (mp4/webm/…) -> direkt abspielen. */
export const parseVideo = (url) => {
  const v = (url || '').trim();
  if (!v) return null;
  let m = v.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([\w-]{11})/i);
  if (m) return { kind: 'embed', provider: 'YouTube', src: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0` };
  m = v.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (m) return { kind: 'embed', provider: 'Vimeo', src: `https://player.vimeo.com/video/${m[1]}?dnt=1` };
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(v) || v.startsWith('/media/')) return { kind: 'file', src: resolveImageUrl(v) };
  return null;
};

const VideoBlock = ({ data }) => {
  const video = parseVideo(data.url);
  const [loaded, setLoaded] = useState(false);
  if (!video) return null;
  const ratio = RATIO[data.ratio || '16:9'] || RATIO['16:9'];
  return (
    <Frame data={data} defaultWidth="narrow">
      <div className={`${ratio} w-full rounded-xl overflow-hidden bg-black relative`}>
        {video.kind === 'file' ? (
          <video src={video.src} controls playsInline preload="metadata" className="w-full h-full" />
        ) : loaded ? (
          <iframe src={video.src} title={data.caption || `${video.provider}-Video`} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
        ) : (
          // Datenschutz: erst nach Klick wird etwas vom Anbieter geladen.
          <button type="button" onClick={() => setLoaded(true)} className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-3 text-white px-4 text-center bg-[#231f1f]">
            <span className="w-16 h-16 rounded-full bg-[#0094ff] flex items-center justify-center"><Play className="w-7 h-7 ml-1" /></span>
            <span className="font-medium">Video abspielen</span>
            <span className="text-xs text-white/70 max-w-sm">Mit dem Abspielen werden Daten an {video.provider} übertragen.</span>
          </button>
        )}
      </div>
      {data.caption && <p className="text-[#a1a1aa] text-sm mt-2 text-center">{data.caption}</p>}
    </Frame>
  );
};

const StatsBlock = ({ data }) => {
  const items = (data.items || []).filter((s) => s && (s.value || s.label));
  if (items.length === 0) return null;
  return (
    <Frame data={data} defaultSpace="lg">
      <SectionTitle data={data} />
      <div className={`grid gap-4 ${COLS[Math.min(4, Math.max(2, items.length))]}`}>
        {items.map((s, i) => (
          <div key={i} className="bg-[#231f1f] border border-[#605e5e]/10 rounded-xl p-5 text-center">
            <div className="text-3xl font-bold text-[#0094ff]">{s.value}</div>
            <div className="text-[#a1a1aa] text-sm mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </Frame>
  );
};

const SPACER = { sm: 'h-4', md: 'h-8', lg: 'h-16', xl: 'h-28' };
const SpacerBlock = ({ data }) => <div className={SPACER[data.height || 'md'] || SPACER.md} aria-hidden="true" />;

const DividerBlock = () => <hr className="border-[#605e5e]/20 my-8" />;

const InfoBoxBlock = ({ data }) => {
  if (!data.title && !data.text) return null;
  return (
    <Frame data={data}>
      <div className="border rounded-lg p-5" style={{ background: data.bg_color || '#231f1f', borderColor: data.border_color || 'rgba(96,94,94,0.2)' }}>
        <div className="flex items-start gap-3">
          {data.show_icon !== false && <Info className="w-5 h-5 text-[#0094ff] shrink-0 mt-0.5" />}
          <div className="flex-1">
            {data.title && <h3 className="font-semibold text-base mb-1.5" style={{ color: data.title_color || '#fff' }}>{data.title}</h3>}
            {data.text && <p className="leading-relaxed text-sm whitespace-pre-line" style={{ color: data.text_color || '#a1a1aa' }}>{data.text}</p>}
            {data.link && (
              <SmartLink href={data.link} className="inline-flex items-center gap-1 mt-2 text-[#0094ff] text-sm font-medium hover:underline">
                {data.link_label || 'Mehr erfahren'} <ArrowRight className="w-3 h-3" />
              </SmartLink>
            )}
          </div>
        </div>
      </div>
    </Frame>
  );
};

const ColumnsBlock = ({ data }) => {
  const cols = (data.columns || []).filter((c) => c && c.html);
  if (cols.length === 0) return null;
  return (
    <Frame data={data}>
      <div className={`grid gap-8 ${COLS[Math.min(3, cols.length)]}`}>
        {cols.map((c, i) => (
          <div key={i} className="article-content text-[#a1a1aa]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.html) }} />
        ))}
      </div>
    </Frame>
  );
};

const ContactBlock = ({ data }) => {
  const ctx = useSiteSettings();
  const biz = (ctx && ctx.getBusinessInfo && ctx.getBusinessInfo()) || {};
  const address = [biz.street, [biz.zip, biz.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const rows = [
    data.show_phone !== false && biz.phone && { icon: Phone, text: biz.phone, href: `tel:${biz.phone.replace(/[^+\d]/g, '')}` },
    data.show_email !== false && biz.email && { icon: Mail, text: biz.email, href: `mailto:${biz.email}` },
    data.show_address !== false && address && { icon: MapPin, text: address },
  ].filter(Boolean);
  if (rows.length === 0 && !data.title) return null;
  return (
    <Frame data={data} defaultWidth="narrow">
      <div className="bg-[#231f1f] border border-[#605e5e]/10 rounded-xl p-6 text-center">
        {data.title && <h3 className="text-white font-semibold text-xl mb-4">{data.title}</h3>}
        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-center gap-2 text-[#a1a1aa]">
              <r.icon className="w-4 h-4 text-[#0094ff]" />
              {r.href ? <a href={r.href} className="hover:text-white">{r.text}</a> : <span>{r.text}</span>}
            </li>
          ))}
        </ul>
        {data.button_label && <div className="mt-5"><BlockButton label={data.button_label} link={data.button_link || '/kontakt'} /></div>}
      </div>
    </Frame>
  );
};

const BLOCK_MAP = {
  heading: HeadingBlock,
  text: TextBlock,
  image: ImageBlock,
  gallery: GalleryBlock,
  imagetext: ImageTextBlock,
  button: ButtonBlock,
  banner: BannerBlock,
  hero: HeroBlock,
  news: NewsBlock,
  cards: CardsBlock,
  quote: QuoteBlock,
  faq: FaqBlock,
  video: VideoBlock,
  stats: StatsBlock,
  spacer: SpacerBlock,
  divider: DividerBlock,
  infobox: InfoBoxBlock,
  columns: ColumnsBlock,
  contact: ContactBlock,
  promos: PromosBlock,
};

/** Rendert genau einen Block `{ id, type, data }`. */
export const Block = ({ block }) => {
  const Component = BLOCK_MAP[block.type];
  if (!Component) return null;
  return <Component data={block.data || {}} />;
};

/** Renders an ordered list of page-content blocks stored under `site_settings.pages.<page>.blocks`. */
export const BlockRenderer = ({ blocks, fallback = null }) => {
  if (!blocks || blocks.length === 0) return fallback;
  return (
    <>
      {blocks.map((block) => <Block key={block.id} block={block} />)}
    </>
  );
};

export default BlockRenderer;
